import {inject, NgModule} from '@angular/core';
import {PreloadAllModules, ResolveFn, RouteReuseStrategy, RouterModule, Routes} from '@angular/router';
import {CustomRouteReuseStrategy} from './shared/strategies/custom-route-reuse-strategy';
import {SystemComponent} from './system/system.component';
import {Tab} from '@common-module/app-enums/tab';
import {ReportSettings} from "@common-module/modelinterfaces/report-settings.model";
import {Observable} from "rxjs";
import {ReportSettingsStorageService} from "./shared/services/report-settings-storage.service";
import {permissionGuard} from "./shared/guards/permission.guard";
import {FeatureKey} from "@common-module/modelinterfaces/enums/feature-key";
import {LogoutComponent} from "./auth/logout/logout.component";
import {AdminLoginFormComponent} from "./auth/admin-login-form/admin-login-form.component";
import {authGuard} from "./shared/guards/auth.guard";

export const initReportSettingsStorageResolver: ResolveFn<ReportSettings[]> = (): Observable<ReportSettings[]> => {
  const dataService = inject(ReportSettingsStorageService);
  return dataService.init();
};

const routes: Routes = [
  {
    path: Tab.ACT_AS,
    component: AdminLoginFormComponent
  },
  {
    path: Tab.LOGOUT,
    component: LogoutComponent,
  },
  {
    path: Tab.TELEGRAM,
    canActivate: [authGuard],
    loadChildren: () => import('./telegram-activation/telegram.module').then(module => module.TelegramModule)
  },
  {
    path: Tab.LOGISTIC,
    canActivate: [permissionGuard(FeatureKey.LOGISTIC), authGuard],
    loadChildren: () => import('./system/logistic/logistic.module').then(module => module.LogisticModule)
  },
  {
    path: '',
    component: SystemComponent,
    canActivate: [authGuard],
    resolve: {
      reportSettings: initReportSettingsStorageResolver
    },
    children: [
      {
        path: '',
        redirectTo: Tab.MAP,
        pathMatch: 'full'
      },
      {
        path: Tab.MAP,
        canActivate: [permissionGuard(FeatureKey.MAP)],
        loadChildren: () => import('./system/main/map/map.module').then(module => module.MapModule)
      },
      {
        path: Tab.ECO_DRIVING,
        canActivate: [permissionGuard(FeatureKey.ECODRIVING)],
        loadChildren: () => import('./system/main/eco-driving/eco-driving.module').then(module => module.EcoDrivingModule)
      },
      {
        path: Tab.DRIVERS,
        canActivate: [permissionGuard(FeatureKey.DRIVERS)],
        loadChildren: () => import('./system/main/drivers/drivers.module').then(module => module.DriversModule)
      },
      {
        path: Tab.SENSORS,
        canActivate: [permissionGuard(FeatureKey.SENSORS)],
        loadChildren: () => import('./system/main/sensors/sensors.module').then(module => module.SensorsModule)
      },
      {
        path: Tab.REPORT,
        canActivate: [permissionGuard(FeatureKey.REPORT)],
        loadChildren: () => import('./system/main/report/report.module').then(module => module.ReportModule)
      },
      {
        path: Tab.CHART,
        canActivate: [permissionGuard(FeatureKey.CHART)],
        loadChildren: () => import('./system/main/chart/chart.module').then(module => module.ChartModule)
      },
      {
        path: Tab.VEHICLE,
        canActivate: [permissionGuard(FeatureKey.FLEET)],
        loadChildren: () => import('./system/main/vehicle/vehicle.module').then(module => module.VehicleModule)
      },
      {
        path: Tab.NOTIFICATION,
        canActivate: [permissionGuard(FeatureKey.NOTIFICATION)],
        loadChildren: () => import('./system/main/notification/notification.module').then(module => module.NotificationModule)
      },
      {
        path: Tab.VIDEO,
        canActivate: [permissionGuard(FeatureKey.VIDEO)],
        loadChildren: () => import('./system/main/video/video.module').then(module => module.VideoModule)
      },
      {
        path: Tab.PUBLIC,
        loadChildren: () => import('./system/public/public.module').then(module => module.PublicModule)
      },
      {
        path: Tab.ACCESS_DENIED,
        loadComponent: () => import('./system/access-denied/access-denied.component').then(c => c.AccessDeniedComponent)
      },
      {
        path: Tab.ROUTES,
        loadChildren: () => import('./system/main/routes/routes.routes').then(m => m.ROUTES_ROUTES)
      },
      {
        path: Tab.RUNS,
        loadChildren: () => import('./system/main/run/runs.routes').then(m => m.RUNS_ROUTES)
      },
      {
        path: Tab.GEOFENCES,
        canActivate: [permissionGuard(FeatureKey.MAP)],
        loadChildren: () => import('./system/main/geofence-import/geofence-import.routes')
          .then(module => module.GEOFENCE_IMPORT_ROUTES)
      },
      {
        path: Tab.SEASON_CALENDAR,
        loadChildren: () => import('./system/main/season-calendar/season-calendar.routes')
          .then(module => module.SEASON_CALENDAR_ROUTES)
      }
    ]
  },
  {path: '**', redirectTo: Tab.MAP}
];

@NgModule({
  imports: [RouterModule.forRoot(routes,
    {
      preloadingStrategy: PreloadAllModules
    }
  )],
  exports: [RouterModule],
  providers: [
    {provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy}
  ]
})

export class AppRoutingModule {
}
